import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../components/Toasts'
import { ErrorState } from '../components/States'
import { RoleForm } from './RoleForm'
import { readFileText, isPdf, looksLikeSpreadsheet, isDocumentFile } from '../lib/readfile'
import { SAMPLE_JD_TEXT } from '../api/mock/extract'
import type { SaveStatus } from '../types'
import { BackButton } from '../components/BackButton'
import { blankForm, createBody, formFromUpload, formProblem } from '../lib/role-draft'
import type { ReadFields, RoleFormState } from '../lib/role-draft'

/**
 * Extraction can take up to a minute, so the wait is designed rather than left
 * as a spinner: the copy moves through what is actually happening, and it gives
 * up with something useful to do if the job stalls.
 */
const PROGRESS_COPY: [number, string][] = [
  [0, 'Uploading the document…'],
  [3, 'Reading the pages…'],
  [12, 'Pulling out the title, level and location…'],
  [25, 'Pulling out responsibilities and skills…'],
  [40, 'Still reading. Long documents take a little longer…'],
]
const STALL_SECONDS = 90

type Stage = 'pick' | 'extracting' | 'review'

export function NewRole() {
  const navigate = useNavigate()
  const { me } = useAuth()
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  const families = me?.settings.jobFamilies ?? []
  const jobId = params.get('job')

  const [stage, setStage] = useState<Stage>(jobId ? 'extracting' : 'pick')
  const [text, setText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')
  const [dragging, setDragging] = useState(false)
  // The form, and what an upload read, carried unseen into the save (FR-49).
  const [draft, setDraft] = useState<RoleFormState>(() => blankForm(''))
  const [read, setRead] = useState<ReadFields | undefined>(undefined)
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const startedAt = useRef(Date.now())

  const back = () => navigate('/job-descriptions')

  const clearJob = useCallback(() => {
    const next = new URLSearchParams(params)
    next.delete('job')
    setParams(next, { replace: true })
  }, [params, setParams])

  // Polling is keyed off the job id in the address, so navigating away and
  // coming back picks the same extraction up rather than losing it.
  useEffect(() => {
    if (!jobId) return
    let live = true
    startedAt.current = Date.now()
    setStage('extracting')
    setError('')

    const tick = setInterval(() => {
      if (live) setElapsed(Math.round((Date.now() - startedAt.current) / 1000))
    }, 1000)

    const poll = async () => {
      while (live) {
        try {
          const job = await api.getUpload(jobId)
          if (!live) return
          if (job.status === 'ready' && job.draft) {
            const fromUpload = formFromUpload(job.draft, families[0] || '')
            setDraft(fromUpload.form)
            setRead(fromUpload.read)
            setFileName(job.fileName)
            setStage('review')
            clearJob()
            return
          }
          if (job.status === 'failed') {
            setError(job.error ?? 'That document could not be read.')
            setStage('pick')
            clearJob()
            return
          }
        } catch (err) {
          if (!live) return
          setError(err instanceof Error ? err.message : 'The upload stopped responding.')
          setStage('pick')
          clearJob()
          return
        }
        await new Promise((r) => setTimeout(r, 1200))
      }
    }
    void poll()

    return () => {
      live = false
      clearInterval(tick)
    }
    // families and clearJob are stable enough for this effect's purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  /** Said in one place, because the file path and the paste path both need it. */
  const sendToMassUpload = (what: string) => {
    const message =
      `${what} This page adds one job description. Use mass upload for a file holding several, ` +
      'which reads every role in it and lets you check each one before importing.'
    setFile(null)
    setFileName('')
    setError(message)
    toast.fail(message)
  }

  const takeFile = async (picked: File) => {
    setError('')
    // A spreadsheet is refused here rather than sent to be read. It would cost
    // a model call and come back as one role out of however many the file held,
    // which is worse than being told.
    if (looksLikeSpreadsheet(picked.name)) {
      sendToMassUpload(`“${picked.name}” is a spreadsheet.`)
      return
    }
    // The picker's accepted types are a suggestion; dragging a file in ignores
    // them, which is how a Java file was read as a job description.
    if (!isDocumentFile(picked.name)) {
      const message =
        `“${picked.name}” is not a document Stello can read. It takes a Word file, a PDF or ` +
        'a plain text file.'
      setFile(null)
      setFileName('')
      setError(message)
      toast.fail(message)
      return
    }
    setFile(picked)
    setFileName(picked.name)
    try {
      if (isPdf(picked)) {
        // The server reads PDFs, scanned ones included, so there is nothing to
        // show here. The upload carries the file itself.
        setText('')
        toast.ok(`${picked.name} is ready. Press “Read and fill in the form”.`)
        return
      }
      const body = await readFileText(picked)
      // A spreadsheet saved under another extension still reads as one, and the
      // header row is unmistakable.
      if (looksLikeSpreadsheet(picked.name, body)) {
        sendToMassUpload(`“${picked.name}” holds spreadsheet rows, not one written role.`)
        return
      }
      setText(body)
      toast.ok(`Read ${picked.name}. Check the text, then extract it.`)
    } catch (err) {
      const message = err instanceof Error ? err.message : `Could not read ${picked.name}.`
      setFile(null)
      setFileName('')
      setError(message)
      toast.fail(message)
    }
  }

  const startExtraction = async () => {
    if (!text.trim() && !file) {
      toast.fail('Choose a document or paste the text first.')
      return
    }
    // Pasted spreadsheet rows are the same mistake by another route.
    if (looksLikeSpreadsheet('', text)) {
      sendToMassUpload('Those are spreadsheet rows, not one written role.')
      return
    }
    setError('')
    try {
      const job = await api.startExtraction(file, text)
      const next = new URLSearchParams(params)
      next.set('job', job.id)
      setParams(next, { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The upload did not start.'
      setError(message)
      toast.fail(message)
    }
  }

  const startBlank = () => {
    setDraft(blankForm(families[0] ?? ''))
    setRead(undefined)
    setFile(null)
    setFileName('')
    setStage('review')
  }

  const save = async (status: SaveStatus) => {
    const problem = formProblem(draft)
    if (problem) {
      toast.fail(problem)
      return
    }
    setSaving(true)
    try {
      const jd = await api.createJobDescription(createBody(draft, read), status)
      toast.ok(
        status === 'Published'
          ? `Published “${jd.title}”`
          : `Saved “${jd.title}” as a draft. Nobody else sees it until you publish it.`,
      )
      navigate(`/job-descriptions/${jd.id}`)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not save.')
    } finally {
      setSaving(false)
    }
  }

  const progressLine =
    [...PROGRESS_COPY].reverse().find(([at]) => elapsed >= at)?.[1] ?? PROGRESS_COPY[0][1]
  const stalled = elapsed >= STALL_SECONDS

  return (
    <div className="screen screen--form" style={{ paddingTop: 28 }}>
      <BackButton to="all job descriptions" onClick={back} />
      <h1 className="page page--sub">New job description</h1>

      {stage === 'pick' && (
        <>
          {error && <ErrorState message={error} />}

          <div
            className={`dropzone${dragging ? ' dropzone--over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              const f = e.dataTransfer.files?.[0]
              if (f) void takeFile(f)
            }}
          >
            <h2>From a file or pasted text</h2>
            <p>Drop a Word file, PDF or plain text file here, or choose one below.</p>
            <label className="file-btn">
              ↑ Choose a file
              <input
                type="file"
                accept=".docx,.pdf,.txt,.md,.text"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) void takeFile(f)
                  e.target.value = ''
                }}
              />
            </label>

            {fileName && (
              <div className="banner banner--ok" style={{ marginTop: 14, justifyContent: 'center' }}>
                <span aria-hidden="true">✓</span> {fileName} is ready
              </div>
            )}

            <p style={{ margin: '16px 0 0' }}>
              Or paste the text of the role. Stello fills in the form either way.
            </p>
            <label className="sr-only" htmlFor="jd-paste">Paste the job description text</label>
            <textarea
              id="jd-paste"
              rows={7}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'Title: Staff Robotics Engineer\nLevel: Staff\nLocation: Detroit, MI\nSummary: …\n- Owns the control stack for…\nSkills: C++17, ROS 2, Control theory'}
            />
          </div>

          <div className="actions">
            <button type="button" className="btn btn--primary" onClick={() => void startExtraction()}>
              Read and fill in the form
            </button>
            <button type="button" className="btn" onClick={() => setText(SAMPLE_JD_TEXT)}>
              Use an example
            </button>
            <button type="button" className="btn" onClick={startBlank}>
              Start from an empty form
            </button>
          </div>
        </>
      )}

      {stage === 'extracting' && (
        <div className="card state" aria-live="polite" aria-busy={!stalled}>
          {!stalled && <div className="spinner" aria-hidden="true" />}
          <h2>{stalled ? 'This is taking longer than it should' : 'Reading the document'}</h2>
          <p>
            {stalled
              ? `It has been ${elapsed} seconds. The job may still finish on its own, but you do not have to wait. Go back and try again, or paste the text instead.`
              : progressLine}
          </p>
          <p className="hint">
            {stalled ? '' : `${elapsed}s elapsed. Nothing is saved until you check the result.`}
          </p>
          {stalled && (
            <div className="actions" style={{ justifyContent: 'center' }}>
              <button type="button" className="btn" onClick={() => { clearJob(); setStage('pick') }}>
                Stop waiting
              </button>
            </div>
          )}
        </div>
      )}

      {stage === 'review' && (
        <>
          <div className="banner banner--ok">
            <span aria-hidden="true">✓</span>
            {fileName
              ? `Read from ${fileName}, section by section under its own headings. Check it before saving. Nothing is published yet.`
              : 'Fill in the role. Nothing is published until you save.'}
          </div>
          <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <RoleForm draft={draft} onChange={setDraft} families={families} />
            {/* Publishing is the normal case, so it is the obvious button.
                Keeping something back is a deliberate act, offered plainly
                rather than happening on its own. */}
            <div className="actions" style={{ paddingTop: 4 }}>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void save('Published')}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Publish'}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => void save('Draft')}
                disabled={saving}
              >
                Save as a draft
              </button>
              <button type="button" className="btn btn--link" onClick={back} disabled={saving}>
                Discard
              </button>
            </div>
            <p className="hint">
              A draft is saved but not part of the library yet. You can publish it from the role
              at any time.
            </p>
          </div>
        </>
      )}
    </div>
  )
}
